import type { IAdobeHost } from "@hops/adobe-panel-core";
import {
  LoginForm,
  PublishDialog,
  RequestDetail,
  RequestList,
  StatusUpdateDialog,
  TimerBar,
  useAuth,
  usePublish,
  useServerUrl,
  useStartWork,
  useTimer,
} from "@hops/adobe-panel-core";
import {
  getArtworkRequestOptions,
  listArtworkRequestsInfiniteOptions,
  listArtworkStatusCodesOptions,
  updateArtworkStatusMutation,
} from "@hops/sdk/react-query";
import type { ArtworkRequestListItem } from "@hops/sdk/types";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

type View = "queue" | "detail";

interface AppProps {
  host: IAdobeHost;
  defaultServerUrl: string;
}

export function App({ host, defaultServerUrl }: AppProps) {
  const {
    isAuthenticated,
    user,
    isLoading: authLoading,
    error: authError,
    login,
    logout,
  } = useAuth();
  const { serverUrl, setServerUrl } = useServerUrl(defaultServerUrl);
  const timer = useTimer();

  const [view, setView] = useState<View>("queue");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  const { startWork, isStarting, error: startError } = useStartWork({ host });
  const {
    exportArtwork,
    publish,
    isExporting,
    isPublishing,
    error: publishError,
  } = usePublish({ host });

  // Artwork requests list (infinite)
  const requestsQuery = useInfiniteQuery({
    ...listArtworkRequestsInfiniteOptions({ query: { pageSize: 25 } }),
    initialPageParam: 1 as string | number,
    getNextPageParam: (lastPage) => {
      const np = lastPage.nextPage;
      if (np == null) return undefined;
      const n = Number(np);
      return Number.isNaN(n) ? undefined : n;
    },
    enabled: isAuthenticated,
  });

  const allItems = useMemo(
    () => requestsQuery.data?.pages.flatMap((page) => page.items ?? []) ?? [],
    [requestsQuery.data],
  );

  // Single request detail
  const detailQuery = useQuery({
    ...getArtworkRequestOptions({ path: { id: selectedId as number } }),
    enabled: isAuthenticated && selectedId != null,
  });

  // Status codes for status update dialog
  const statusCodesQuery = useQuery({
    ...listArtworkStatusCodesOptions(),
    enabled: isAuthenticated,
  });

  const statusMutation = useMutation(updateArtworkStatusMutation());

  // Navigation
  const handleSelectRequest = useCallback((item: ArtworkRequestListItem) => {
    setSelectedId(Number(item.id));
    setView("detail");
  }, []);

  const handleBack = useCallback(() => {
    setView("queue");
    setSelectedId(null);
  }, []);

  // Workflows
  const handleStartWork = useCallback(async () => {
    if (!detailQuery.data) return;
    const ok = await startWork(detailQuery.data);
    if (ok) {
      detailQuery.refetch();
    }
  }, [detailQuery.data, detailQuery.refetch, startWork]);

  const handlePublishClick = useCallback(async () => {
    if (!detailQuery.data?.requestId) return;
    await exportArtwork(detailQuery.data.requestId);
    setShowPublish(true);
  }, [detailQuery.data, exportArtwork]);

  const handlePublishConfirm = useCallback(
    async (data: { totalMinutes: number; notes: string }) => {
      if (!detailQuery.data) return;
      const ok = await publish(
        Number(detailQuery.data.id),
        detailQuery.data.requestId ?? String(detailQuery.data.id),
        data,
      );
      if (ok) {
        setShowPublish(false);
        handleBack();
      }
    },
    [detailQuery.data, publish, handleBack],
  );

  const handleStatusSubmit = useCallback(
    async (statusCode: string, note: string) => {
      if (!selectedId) return;
      await statusMutation.mutateAsync({
        path: { id: selectedId },
        body: {
          statusCode,
          note: note || null,
          attachmentUrl: null,
          attachmentThumb: null,
          sendEmail: null,
        },
      });
      setShowStatus(false);
      detailQuery.refetch();
      requestsQuery.refetch();
    },
    [selectedId, statusMutation, detailQuery, requestsQuery],
  );

  // Auth gate
  if (!isAuthenticated) {
    return (
      <LoginForm
        onLogin={async (u, p) => {
          await login(u, p);
        }}
        isLoading={authLoading}
        error={authError}
        serverUrl={serverUrl}
        onServerUrlChange={setServerUrl}
      />
    );
  }

  const request = detailQuery.data;
  const canStartWork =
    request != null &&
    request.statusCode !== "COMP" &&
    request.statusCode !== "CANC";

  return (
    <div className="panel-root">
      {/* Header */}
      <div className="panel-header">
        <span className="panel-title">HOPS Artwork</span>
        <button
          className="hops-btn hops-btn-ghost hops-btn-sm"
          onClick={logout}
          title="Sign out"
        >
          {user?.username}
        </button>
      </div>

      {/* Content */}
      <div className="panel-content">
        {view === "queue" && (
          <RequestList
            items={allItems}
            currentUserId={user?.id}
            isLoading={
              requestsQuery.isLoading || requestsQuery.isFetchingNextPage
            }
            hasNextPage={requestsQuery.hasNextPage}
            onLoadMore={() => requestsQuery.fetchNextPage()}
            onSelect={handleSelectRequest}
            onRefresh={() => requestsQuery.refetch()}
          />
        )}

        {view === "detail" && request && (
          <RequestDetail
            request={request}
            onBack={handleBack}
            onStartWork={handleStartWork}
            onPublish={handlePublishClick}
            onUpdateStatus={() => setShowStatus(true)}
            isWorking={isStarting || isExporting}
            canStartWork={canStartWork}
          />
        )}

        {view === "detail" && !request && detailQuery.isLoading && (
          <div className="panel-center-message">Loading...</div>
        )}

        {startError && <div className="panel-error">{startError}</div>}
        {publishError && <div className="panel-error">{publishError}</div>}
      </div>

      {/* Timer bar */}
      <TimerBar
        requestId={timer.requestId}
        isRunning={timer.isRunning}
        getElapsedMs={timer.getElapsedMs}
        onPause={timer.pause}
        onResume={timer.resume}
      />

      {/* Dialogs */}
      <PublishDialog
        open={showPublish}
        onClose={() => setShowPublish(false)}
        onSubmit={handlePublishConfirm}
        requestId={request?.requestId ?? null}
        elapsedMinutes={timer.elapsedMinutes}
        isLoading={isPublishing}
      />

      <StatusUpdateDialog
        open={showStatus}
        onClose={() => setShowStatus(false)}
        onSubmit={handleStatusSubmit}
        statusCodes={statusCodesQuery.data ?? []}
        currentStatusCode={request?.statusCode}
        isLoading={statusMutation.isPending}
      />
    </div>
  );
}
